/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import PropTypes from 'prop-types';
import React, {useMemo, useCallback, useEffect, useState } from 'react';
import { Marked } from 'marked';
import { gfmHeadingId } from 'marked-gfm-heading-id';
import { mangle } from 'marked-mangle';
import DOMPurify from 'dompurify';
import classNames from 'classnames';
import { MarkupTypes } from 'utils/MarkupTypes';

const Markdown = ({ data, className, markupType, ...otherProps }) => {

  const marked = useMemo(() => new Marked(), []);
  marked.use(mangle());
  const options = {
    prefix: ''
  };

  marked.use(gfmHeadingId(options));
  const parseData = useCallback((data, type) => {
    if (!data) {
      return;
    }
    if (type === MarkupTypes.markdown) {
      // Use marked for swagger 1.0 and 2.0
      return DOMPurify.sanitize(marked.parse(data));
    } else {
      // Use markdown-it (implements commonmark spec)
      let md = require('markdown-it')('commonmark');
      return DOMPurify.sanitize(md.render(data));
    }
  }, [marked]);
  const [convertedData, updateConvertedData] = useState(() => parseData(data, markupType));

  useEffect(() => {
    updateConvertedData(parseData(data, markupType));
  }, [data, markupType, parseData]);

  return <div className={classNames('markdown', className)} data-testid={'markdown'} dangerouslySetInnerHTML={{ __html: convertedData }} {...otherProps} />;
};

Markdown.propTypes = {
  data: PropTypes.string,
  className: PropTypes.string,
  markupType: PropTypes.oneOf(Object.values(MarkupTypes))
};

export default Markdown;
